/**
 * ScheduleActions — Contractor-side schedule controls inside the workspace.
 *
 * Drives the auto-cascade by giving contractors three primary actions:
 *   1. Confirm "On Track" — closes the cadence ping
 *   2. Push my dates    — rippling new start/end downstream automatically
 *   3. Message the owner — opens a quick reply thread (reuses the existing
 *      contractor-portal/messages endpoint)
 *
 * All three are surfaced as buttons on the workspace home page so contractors
 * never need to open email to keep the project schedule current.
 */
import React, { useState } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { CheckCircle2, Calendar, MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const fmtDate = (d) => {
  if (!d) return '—';
  return formatDate(d) || '—';
};

export default function ScheduleActions({ data, phase, projectId, onUpdated }) {
  const me = data?.contractor;
  const [confirmingOnTrack, setConfirmingOnTrack] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);

  const onConfirmOnTrack = async () => {
    if (!phase?.id || !projectId) return;
    setConfirmingOnTrack(true);
    try {
      await api.post('/cadence/respond', {
        project_id: projectId,
        contractor_id: me.id,
        phase_id: phase.id,
        response: 'on_track',
      });
      toast.success('Owner notified — your dates are confirmed.');
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm');
    } finally {
      setConfirmingOnTrack(false);
    }
  };

  return (
    <>
      <Card
        className="p-4 bg-white border-slate-200"
        data-testid="schedule-actions-card"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Keep the schedule accurate</p>
            <h3 className="text-sm font-bold text-slate-900 mt-0.5">
              Your phase runs <span className="text-blue-700">{fmtDate(phase?.start_date)}</span> → <span className="text-blue-700">{fmtDate(phase?.end_date)}</span>
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Pushing your dates auto-shifts every downstream contractor too — they get notified for you.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={onConfirmOnTrack}
            disabled={confirmingOnTrack}
            data-testid="schedule-on-track-btn"
          >
            {confirmingOnTrack
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
            I'm on track
          </Button>
          <Button
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => setPushOpen(true)}
            data-testid="schedule-push-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            Push my dates
          </Button>
          <Button
            variant="outline"
            onClick={() => setMsgOpen(true)}
            data-testid="schedule-message-btn"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
            Message owner
          </Button>
        </div>
      </Card>

      <PushDatesDialog
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        phase={phase}
        contractorId={me?.id}
        projectId={projectId}
        onSuccess={() => { setPushOpen(false); onUpdated && onUpdated(); }}
      />

      <MessageOwnerDialog
        open={msgOpen}
        onClose={() => setMsgOpen(false)}
        contractor={me}
        projectId={projectId}
        onSuccess={() => setMsgOpen(false)}
      />
    </>
  );
}


function PushDatesDialog({ open, onClose, phase, contractorId, projectId, onSuccess }) {
  const [form, setForm] = useState({ new_start: '', new_end: '', note: '' });
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (open && phase) {
      const iso = (d) => (d ? String(d).split('T')[0] : '');
      setForm({ new_start: iso(phase.start_date), new_end: iso(phase.end_date), note: '' });
    }
  }, [open, phase]);

  const submit = async () => {
    if (!form.new_start || !form.new_end) return toast.error('Both new start and end are required');
    setSending(true);
    try {
      const res = await api.post('/cadence/respond', {
        project_id: projectId,
        contractor_id: contractorId,
        phase_id: phase.id,
        response: 'push',
        new_start_date: form.new_start,
        new_end_date: form.new_end,
        note: form.note || null,
      });
      const downstreamCount = (res.data?.shifted || []).length - 1; // minus the responding phase itself
      toast.success(
        downstreamCount > 0
          ? `Schedule updated — ${downstreamCount} downstream contractor${downstreamCount === 1 ? '' : 's'} notified.`
          : 'Schedule updated.'
      );
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not push schedule');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="push-dates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-600" />Push your dates</DialogTitle>
          <DialogDescription>
            Updating your dates auto-shifts every downstream contractor. They'll get an email with the new schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">New start</Label>
              <Input type="date" value={form.new_start} onChange={(e) => setForm({ ...form, new_start: e.target.value })} data-testid="push-new-start" />
            </div>
            <div>
              <Label className="text-xs">New end</Label>
              <Input type="date" value={form.new_end} onChange={(e) => setForm({ ...form, new_end: e.target.value })} data-testid="push-new-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Material delay, weather, crew availability..."
              data-testid="push-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending} data-testid="push-submit">
            {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Updating…</> : <>Push dates & notify downstream</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MessageOwnerDialog({ open, onClose, contractor, projectId, onSuccess }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim()) return toast.error('Write a message first');
    setSending(true);
    try {
      await api.post(
        `/contractor-portal/messages?email=${encodeURIComponent(contractor?.email)}&project_id=${projectId}`,
        { message }
      );
      toast.success('Message sent to the owner.');
      setMessage('');
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="message-owner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-blue-600" />Message the owner</DialogTitle>
          <DialogDescription>
            Your message gets emailed to the owner immediately and saved to your thread inside DrawReady.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What's going on?"
          data-testid="message-body"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending} data-testid="message-send">
            {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
